/**
 * GitHub and Entra ID OAuth authentication service
 *
 * Supports three identity providers:
 * - "github" (EMU) — for GitHub Enterprise Managed Users
 * - "publicgithub" — for public GitHub users
 * - "entraid" — for Microsoft Entra ID users
 *
 * Backend callbacks at /auth/github/callback, /auth/publicgithub/callback, and /auth/entraId/callback
 * handle client secret/certificate exchange.
 *
 * @module services/authService
 */

import { config } from '@/config';

/**
 * Runtime configuration injected by docker-entrypoint.sh.
 * Falls back to build-time VITE_ env vars for local development.
 */
const runtimeConfig: Record<string, string> =
  ((window as unknown as Record<string, unknown>).__ADC_CONFIG__ as Record<string, string>) ?? {};

const GITHUB_CLIENT_ID = runtimeConfig.GITHUB_CLIENT_ID || import.meta.env.VITE_GITHUB_CLIENT_ID;
const PUBLIC_GITHUB_CLIENT_ID =
  runtimeConfig.PUBLIC_GITHUB_CLIENT_ID || import.meta.env.VITE_PUBLIC_GITHUB_CLIENT_ID;
const ENTRA_ID_CLIENT_ID =
  runtimeConfig.ENTRA_ID_CLIENT_ID || import.meta.env.VITE_ENTRA_ID_CLIENT_ID;
const ENTRA_ID_TENANT_ID =
  runtimeConfig.ENTRA_ID_TENANT_ID || import.meta.env.VITE_ENTRA_ID_TENANT_ID;
const USE_PROD_ENTRA_AUTH = import.meta.env.VITE_USE_PROD_ENTRA_AUTH === 'true';
const BACKEND_API_URL = config.apiBaseUrl;
const GITHUB_CALLBACK_URL =
  runtimeConfig.GITHUB_CALLBACK_URL || import.meta.env.VITE_GITHUB_CALLBACK_URL;
const PUBLIC_GITHUB_CALLBACK_URL =
  runtimeConfig.PUBLIC_GITHUB_CALLBACK_URL || import.meta.env.VITE_PUBLIC_GITHUB_CALLBACK_URL;

// Production Entra OAuth values (for VITE_USE_PROD_ENTRA_AUTH=true)
const PROD_ENTRA_OAUTH_CLIENT_ID = '8bdf6603-4e80-4b34-856c-4ee02dfe8df3';
const PROD_ENTRA_OAUTH_TENANT_ID = '72f988bf-86f1-41af-91ab-2d7cd011db47';
const PROD_ENTRA_OAUTH_REDIRECT_URI = 'https://management.azuredevcompute.io/auth/entraId/callback';

// Production client IDs (justfile defaults) - used to detect if user overrode them
const PROD_GITHUB_CLIENT_ID = 'Iv23licPqE4MFVU9FMW3';
const PROD_PUBLIC_GITHUB_CLIENT_ID = 'Ov23lipDPhaiG47DyK9t';
const PROD_ENTRA_ID_CLIENT_ID = 'f24012ec-bfc7-415c-96f5-38903080bc4d';

// Devcommon fallback values for private dev deployments
const DEVCOMMON_GITHUB_CLIENT_ID = 'Iv23ctINA0nMqPyByl3C';
const DEVCOMMON_PUBLIC_GITHUB_CLIENT_ID = 'Ov23licElA6zxYj2AqyZ';
const DEVCOMMON_ENTRA_ID_CLIENT_ID = '295a45f0-183e-4a26-8a13-fb36f0bad608';
const DEVCOMMON_ENTRA_ID_TENANT_ID = '72f988bf-86f1-41af-91ab-2d7cd011db47';

export type AuthProvider = 'github' | 'publicgithub' | 'entraid';

interface GitHubUser {
  login: string;
  id: number;
  avatar_url: string;
  name: string;
  email: string;
  authType?: 'github' | 'entra_id';
}

/**
 * Check if we're in a test/dev environment (for devcommon fallback)
 */
function isDevEnvironment(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname.includes('azuredevcompute-test.io') ||
    hostname === 'localhost' ||
    hostname.startsWith('127.0.0.1')
  );
}

/**
 * Resolves the OAuth callback URL using three-tier strategy:
 * 1. If callback URL env var is set (.env override), use it
 * 2. If not set and in test/dev environment, use devcommon stamp
 * 3. Otherwise use BACKEND_API_URL (per-deployment)
 * @param isPublicGitHub - Whether this is for public GitHub (vs EMU)
 */
function getCallbackUrl(isPublicGitHub: boolean): string {
  const callbackPath = isPublicGitHub ? '/auth/publicgithub/callback' : '/auth/github/callback';
  const configuredUrl = isPublicGitHub ? PUBLIC_GITHUB_CALLBACK_URL : GITHUB_CALLBACK_URL;

  // If explicitly configured, use it (tier 1: individual dev override)
  if (configuredUrl) {
    return configuredUrl;
  }

  // Check if we're in test/dev environment (tier 2: devcommon)
  if (isDevEnvironment()) {
    return `https://management.devcommon.azuredevcompute-test.io${callbackPath}`;
  }

  // Tier 3: use configured backend URL
  return `${BACKEND_API_URL}${callbackPath}`;
}

/**
 * Resolves the GitHub OAuth client ID using three-tier strategy:
 * 1. If client ID is explicitly configured (not prod default), use it
 * 2. If using prod default and in test/dev environment, use devcommon client ID
 * 3. Otherwise use the configured value (prod default for prod environments)
 * @param isPublicGitHub - Whether this is for public GitHub (vs EMU)
 */
function getGitHubClientId(isPublicGitHub: boolean): string | undefined {
  const configuredClientId = isPublicGitHub ? PUBLIC_GITHUB_CLIENT_ID : GITHUB_CLIENT_ID;
  const prodClientId = isPublicGitHub ? PROD_PUBLIC_GITHUB_CLIENT_ID : PROD_GITHUB_CLIENT_ID;
  const devcommonClientId = isPublicGitHub
    ? DEVCOMMON_PUBLIC_GITHUB_CLIENT_ID
    : DEVCOMMON_GITHUB_CLIENT_ID;

  // If not configured at all, fall back to devcommon in dev environments
  if (!configuredClientId) {
    return isDevEnvironment() ? devcommonClientId : undefined;
  }

  // If configured to prod default but we're in dev environment, use devcommon instead
  if (configuredClientId === prodClientId && isDevEnvironment()) {
    return devcommonClientId;
  }

  // Use whatever is configured
  return configuredClientId;
}

/**
 * Resolves the Entra ID OAuth configuration:
 * 1. If VITE_ENTRA_ID_CLIENT_ID is set to a non-prod value, use it (override)
 * 2. If VITE_USE_PROD_ENTRA_AUTH=true, use production values
 * 3. Otherwise use devcommon values
 */
function getEntraIdConfig(): { clientId: string; tenantId: string } | undefined {
  // Check for explicit override (non-prod client ID)
  const isProdClientId =
    !ENTRA_ID_CLIENT_ID ||
    ENTRA_ID_CLIENT_ID === PROD_ENTRA_ID_CLIENT_ID ||
    ENTRA_ID_CLIENT_ID === PROD_ENTRA_OAUTH_CLIENT_ID;

  if (!isProdClientId && ENTRA_ID_CLIENT_ID && ENTRA_ID_TENANT_ID) {
    return { clientId: ENTRA_ID_CLIENT_ID, tenantId: ENTRA_ID_TENANT_ID };
  }

  // Use prod or devcommon based on env var
  if (USE_PROD_ENTRA_AUTH || !isDevEnvironment()) {
    return { clientId: PROD_ENTRA_OAUTH_CLIENT_ID, tenantId: PROD_ENTRA_OAUTH_TENANT_ID };
  }
  return { clientId: DEVCOMMON_ENTRA_ID_CLIENT_ID, tenantId: DEVCOMMON_ENTRA_ID_TENANT_ID };
}

/**
 * Redirect user to GitHub OAuth authorization page for the specified provider.
 * @param provider - "github" for EMU, "publicgithub" for public GitHub
 */
export function initiateGitHubLogin(provider: AuthProvider = 'github'): void {
  const csrfToken = generateRandomState();
  sessionStorage.setItem('github_oauth_state', csrfToken);

  const isPublicGitHub = provider === 'publicgithub';
  const clientId = getGitHubClientId(isPublicGitHub);
  if (!clientId) {
    console.error('[AuthService] GitHub client ID not configured');
    return;
  }
  const redirectUri = getCallbackUrl(isPublicGitHub);

  // Include frontend URL and management API URL in state so backend can redirect back
  const statePayload = {
    csrf: csrfToken,
    redirect_url: window.location.origin,
    management_url: BACKEND_API_URL,
  };
  const encodedState = btoa(JSON.stringify(statePayload));

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: 'user:email repo read:org copilot',
    state: encodedState,
  });

  window.location.href = `https://github.com/login/oauth/authorize?${params}`;
}

/**
 * Check if the public GitHub login option is available.
 * Returns true if public GitHub client ID is configured or devcommon fallback available.
 */
export function isPublicGitHubEnabled(): boolean {
  return !!getGitHubClientId(true);
}

/**
 * Check if the Entra ID login option is available.
 * Returns true if Entra ID is configured or devcommon fallback available.
 */
export function isEntraIdEnabled(): boolean {
  return !!getEntraIdConfig();
}

/**
 * Redirect user to Entra ID OAuth authorization page.
 */
export function initiateEntraIdLogin(): void {
  const entraConfig = getEntraIdConfig();
  if (!entraConfig) {
    console.error('[AuthService] Entra ID not configured');
    return;
  }

  const csrfToken = generateRandomState();
  sessionStorage.setItem('entra_oauth_state', csrfToken);

  // Use devcommon callback URL in test/dev environments, unless prod auth override is set
  const redirectUri = USE_PROD_ENTRA_AUTH
    ? PROD_ENTRA_OAUTH_REDIRECT_URI
    : isDevEnvironment()
      ? 'https://management.devcommon.azuredevcompute-test.io/auth/entraId/callback'
      : `${BACKEND_API_URL}/auth/entraId/callback`;

  // Include frontend URL and management API URL in state so backend can redirect back
  const statePayload = {
    csrf: csrfToken,
    return_url: window.location.origin,
    management_url: BACKEND_API_URL,
  };
  const encodedState = btoa(JSON.stringify(statePayload));

  // 1P apps (prod) use {clientId}/AzureDevCompute.Portal.ReadWrite.All for OBO access token
  // 3P apps (devcommon) use {clientId}/.default
  const scope =
    USE_PROD_ENTRA_AUTH || !isDevEnvironment()
      ? `openid profile email ${entraConfig.clientId}/AzureDevCompute.Portal.ReadWrite.All`
      : `openid profile email ${entraConfig.clientId}/.default`;

  const params = new URLSearchParams({
    client_id: entraConfig.clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: scope,
    response_mode: 'query',
    state: encodedState,
  });

  window.location.href = `https://login.microsoftonline.com/${entraConfig.tenantId}/oauth2/v2.0/authorize?${params}`;
}

/**
 * Fetch user information from GitHub API
 */
export async function fetchGitHubUser(token: string): Promise<GitHubUser> {
  // Check if token is a JWT (our signed token) or a GitHub token
  if (isJwtToken(token)) {
    console.log('[AuthService] Decoding signed JWT for user info');
    return decodeJwtUserInfo(token);
  }

  // Fallback to GitHub API for raw GitHub tokens (PATs, legacy flow)
  console.log('[AuthService] Fetching user info from GitHub');

  const response = await fetch('https://api.github.com/user', {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch user info');
  }

  const user: GitHubUser = await response.json();
  console.log('[AuthService] User info:', user.login);
  return user;
}

/**
 * Check if a token is a JWT (starts with base64-encoded JSON header)
 */
function isJwtToken(token: string): boolean {
  // JWTs have format: header.payload.signature
  // Header and payload are base64-encoded JSON
  const parts = token.split('.');
  if (parts.length !== 3) return false;

  try {
    // Try to decode the header - should be JSON starting with {"
    const header = atob(parts[0].replace(/-/g, '+').replace(/_/g, '/'));
    return header.startsWith('{');
  } catch {
    return false;
  }
}

/**
 * Decode user info from our signed JWT token
 */
function decodeJwtUserInfo(token: string): GitHubUser {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format');
  }

  try {
    // Decode the payload (second part)
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(payload);

    // Extract claims - our JWT has: auth_type, email, gh_user (username), gh_id (user id)
    // Note: auth_type from backend is PascalCase (e.g., "EntraId", "GitHub", "PublicGitHub")
    const authTypeRaw = (claims.auth_type || '').toLowerCase();
    const user: GitHubUser = {
      login: claims.gh_user || claims.email?.split('@')[0] || 'user',
      id: claims.gh_id ? parseInt(claims.gh_id, 10) : 0,
      avatar_url: `https://avatars.githubusercontent.com/u/${claims.gh_id || 0}`,
      name: claims.gh_user || claims.email?.split('@')[0] || 'User',
      email: claims.email || '',
      authType: authTypeRaw === 'entraid' ? 'entra_id' : 'github',
    };

    return user;
  } catch (err) {
    console.error('[AuthService] Failed to decode JWT:', err);
    throw new Error('Failed to decode user info from token');
  }
}

/**
 * Verify OAuth state to prevent CSRF attacks
 * Decodes the state payload and validates the CSRF token
 * Checks both GitHub and Entra ID state storage
 */
export function verifyOAuthState(returnedState: string, provider?: AuthProvider): boolean {
  // Check both possible state storage locations
  const githubState = sessionStorage.getItem('github_oauth_state');
  const entraState = sessionStorage.getItem('entra_oauth_state');

  // Determine which state to use based on provider or availability
  let savedCsrfToken: string | null = null;
  if (provider === 'entraid') {
    savedCsrfToken = entraState;
    sessionStorage.removeItem('entra_oauth_state');
  } else if (provider === 'github' || provider === 'publicgithub') {
    savedCsrfToken = githubState;
    sessionStorage.removeItem('github_oauth_state');
  } else {
    // Fallback: try both
    savedCsrfToken = githubState || entraState;
    sessionStorage.removeItem('github_oauth_state');
    sessionStorage.removeItem('entra_oauth_state');
  }

  try {
    const statePayload = JSON.parse(atob(returnedState));
    return savedCsrfToken === statePayload.csrf;
  } catch {
    // Fallback for plain state (backwards compatibility)
    return savedCsrfToken === returnedState;
  }
}

/**
 * Generate random state for CSRF protection
 */
function generateRandomState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Store user data in localStorage (cookie-based auth - no token stored client-side)
 * The auth token is stored in an HttpOnly cookie by the management API.
 */
export function saveUserData(user: GitHubUser, provider: AuthProvider = 'github'): void {
  localStorage.setItem('auth_user', JSON.stringify(user));
  localStorage.setItem('auth_provider', provider);
  console.log('[AuthService] User data saved, provider:', provider);
}

/**
 * Store authentication data in localStorage
 * @deprecated Use saveUserData instead - token is now stored in HttpOnly cookie
 */
export function saveAuthData(
  _token: string,
  user: GitHubUser,
  provider: AuthProvider = 'github'
): void {
  // For backward compatibility, still save user data
  // Token is ignored - it's stored in HttpOnly cookie by management API
  localStorage.setItem('auth_user', JSON.stringify(user));
  localStorage.setItem('auth_provider', provider);
  console.log('[AuthService] Auth data saved (token ignored - using cookie), provider:', provider);
}

/**
 * Retrieve stored user data
 * Note: Token is now stored in HttpOnly cookie, not returned here
 */
export function getAuthData(): { user: GitHubUser; provider: AuthProvider } | null {
  const userJson = localStorage.getItem('auth_user');
  const provider = (localStorage.getItem('auth_provider') as AuthProvider) || 'github';

  if (!userJson) {
    return null;
  }

  try {
    const user = JSON.parse(userJson) as GitHubUser;
    return { user, provider };
  } catch {
    return null;
  }
}

/**
 * Get the current auth provider
 */
export function getAuthProvider(): AuthProvider {
  return (localStorage.getItem('auth_provider') as AuthProvider) || 'github';
}

/**
 * Clear authentication data (logout)
 * Clears localStorage user data and calls management API to clear the HttpOnly cookie.
 */
export async function clearAuthData(): Promise<void> {
  localStorage.removeItem('auth_user');
  localStorage.removeItem('auth_provider');
  console.log('[AuthService] Local auth data cleared');

  // Call management API logout endpoint to clear the HttpOnly cookie
  const apiBaseUrl = config.apiBaseUrl;
  try {
    await fetch(`${apiBaseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include', // Send HttpOnly cookie so management API can clear it
    });
    console.log('[AuthService] Management API logout called, cookie cleared');
  } catch (error) {
    console.error('[AuthService] Failed to call management API logout:', error);
    // Continue anyway - local data is already cleared
  }
}

/**
 * Check if user is authenticated (has user data stored)
 * Note: This only checks local storage. The actual auth state depends on the HttpOnly cookie.
 */
export function isAuthenticated(): boolean {
  return getAuthData() !== null;
}
